/**
 * Work Order Import Schemas
 *
 * Shared Zod schemas for work order import payloads.
 * Used by workOrderDirectional.contract.ts for per-event-type endpoints.
 */

import { z } from 'zod';

/**
 * Ignite Core Work Order Import Payload Schema
 * Based on ignite-core-work-order-import/v1.1.0
 */

// Meta schema
const metaSchema = z.object({
  schemaVersion: z.string(),
  source: z.string(),
  sentAt: z.string().datetime(),
  messageId: z.string().uuid(),
  eventType: z.string(),
  // z.enum([
  //   'work_order.new',
  //   'work_order.rejected',
  //   'work_order.allocated',
  //   'work_order.more_info_requested',
  //   'work_order.more_info_received',
  //   'work_order.accepted',
  //   'work_order.duration_challenge',
  //   'work_order.duration_challenge_approved',
  //   'work_order.scheduled',
  //   'work_order.engineer_en_route',
  //   'work_order.engineer_on_site',
  //   'work_order.aborted',
  //   'work_order.engineering_complete',
  //   'work_order.ofs_submitted',
  //   'work_order.ofs_more_info_requested',
  //   'work_order.ofs_withdrawn',
  //   'work_order.ofs_resubmitted',
  //   'work_order.ofs_approved',
  //   'work_order.dfe_submitted',
  //   'work_order.dfe_more_info_requested',
  //   'work_order.dfe_withdrawn',
  //   'work_order.dfe_resubmitted',
  //   'work_order.dfe_approved',
  //   'work_order.completed',
  // ]),
  endpoint: z.string().url().optional(),
  igniteEventId: z.string().optional(),
});

// Work Order nested schemas
const noteSchema = z.object({
  note: z.string().max(2000),
  createdDate: z.string().datetime(),
  createdBy: z.string().max(150),
});

const clientSchema = z.object({
  name: z.string().max(100).nullable().optional(),
  code: z.string().max(20).nullable().optional(),
  contractReference: z.string().max(100).nullable().optional(),
});

const coordinatesSchema = z.object({
  latitude: z.number().min(49.0).max(61.0).nullable(),
  longitude: z.number().min(-8.0).max(2.0).nullable(),
});

const osCoordinatesSchema = z.object({
  easting: z.number().optional(),
  northing: z.number().optional(),
  raw: z.string().max(50).optional(),
});

const locationSchema = z.object({
  buildingName: z.string().max(100).optional(),
  houseName: z.string().max(100).optional(),
  streetName: z.string().max(150).nullable().optional(),
  district: z.string().max(100).optional(),
  town: z.string().max(100).nullable().optional(),
  county: z.string().max(100).nullable().optional(),
  country: z.string().max(60).nullable().optional(),
  postcode: z.string().max(10),
  fullAddress: z.string().max(500).nullable().optional(),
  usrn: z.number().nullable().optional(),
  coordinates: coordinatesSchema.nullable().optional(),
  osCoordinates: osCoordinatesSchema.nullable().optional(),
});

const scheduleSchema = z.object({
  windowStartDate: z.string().datetime().optional(),
  windowEndDate: z.string().datetime().optional(),
  requestedCompletionDate: z.string().datetime().optional(),
  slaCompletionDate: z.string().datetime().nullable().optional(),
  kciCompletionDate: z.string().datetime().optional(),
  allocationDate: z.string().datetime().nullable().optional(),
  duration: z.number().min(0).optional(),
  durationUnit: z.enum(['minutes', 'hours', 'days']).nullable().optional(),
});

const customerSchema = z.object({
  name: z.string().max(150).optional(),
  phone: z.string().max(30).nullable().optional(),
  email: z.string().email().max(150).nullable().optional(),
});

const psrSchema = z.object({
  fieldIdentified: z.boolean().nullable().optional(),
  iid: z.boolean().nullable().optional(),
  xip: z.string().max(10).optional(),
});

const routingSchema = z.object({
  subcontractorId: z.string().nullable().optional(),
  subcontractorName: z.string().max(150).nullable().optional(),
  operationalUnitCode: z.string().max(50).nullable().optional(),
  routedAt: z.string().datetime().nullable().optional(),
});

const lineItemSchema = z.object({
  code: z.string().max(20),
  category: z.string().max(50).optional(),
  description: z.string().max(200),
  quantity: z.number().min(0).optional(),
  unitCost: z.number().min(0).optional(),
  totalCost: z.number().min(0),
});

const costSchema = z.object({
  purchaseOrderNumber: z.string().max(100).optional(),
  glType: z.string().max(50).optional(),
  estimateNumber: z.string().max(100).optional(),
  currency: z.string().max(5).optional(),
  lineItems: z.array(lineItemSchema).optional(),
});

const workOrderSchema = z.object({
  id: z.string(),
  orderNumber: z.string().max(250).nullable().optional(),
  originatorId: z.string().optional(),
  sapOrderNumber: z.string().max(30).nullable().optional(),
  sapId: z.string().max(60).nullable().optional(),
  orderType: z.string().nullable(),
  activityNumber: z.string().max(20).nullable().optional(),
  taskType: z.string().max(30).nullable().optional(),
  jobType: z.string().max(200).nullable().optional(),
  jobDescription: z.string().max(500).nullable().optional(),
  priority: z.string().optional(),
  status: z.string().optional(),
  lastSiteReport: z.string().max(2000).optional(),
  notes: z.array(noteSchema).optional(),
  client: clientSchema.optional(),
  location: locationSchema,
  schedule: scheduleSchema.optional(),
  customer: z.array(customerSchema).optional(),
  psr: psrSchema.optional(),
  routing: routingSchema.optional(),
  cost: costSchema.optional(),
});

// Permit schema
const permitDatesSchema = z.object({
  registeredStartDate: z.string().datetime().nullable().optional(),
  registeredEndDate: z.string().datetime().nullable().optional(),
  statutoryEstimatedDuration: z.string().datetime().nullable().optional(),
  siteStartDate: z.string().datetime().nullable().optional(),
});

const sensitivityFlagsSchema = z.object({
  protectedStreet: z.boolean().optional(),
  trafficSensitive: z.boolean().optional(),
  engineeringDifficulty: z.boolean().optional(),
  proposedSed: z.boolean().optional(),
  lcsz: z.boolean().optional(),
  esa: z.boolean().optional(),
  structures: z.boolean().optional(),
  pipelines: z.boolean().optional(),
  priorityLanes: z.boolean().optional(),
  laneRental: z.boolean().optional(),
  stenfi: z.boolean().optional(),
  specialEvents: z.boolean().optional(),
  parkingBays: z.boolean().optional(),
  pctsas: z.boolean().optional(),
  taca: z.boolean().optional(),
  speedLimits: z.boolean().optional(),
  strategicRoute: z.boolean().optional(),
  streetLighting: z.boolean().optional(),
  drainageFloodRisk: z.boolean().optional(),
  unusualTraffic: z.boolean().optional(),
  localConsiderations: z.boolean().optional(),
  winterMaintRoutes: z.boolean().optional(),
  hgvApprovedRoutes: z.boolean().optional(),
  emergencyServiceRoutes: z.boolean().optional(),
});

const permitSchema = z.object({
  mobileKey: z.string().max(32),
  nsgTaskType: z.string().max(20),
  nsgStatus: z.string().max(30).optional(),
  taskExternalRefId: z.string().max(60).optional(),
  dummyRefId: z.string().max(30).optional(),
  provisionalFlag: z.boolean().optional(),
  usrn: z.string().max(20),
  street: z.string().max(150).optional(),
  town: z.string().max(60).optional(),
  county: z.string().max(60).optional(),
  streetOwnerCode: z.string().max(20).optional(),
  streetOwnerName: z.string().max(60).optional(),
  streetOwnerCodes: z.array(z.string().max(30)).optional(),
  phaseNumbers: z.array(z.string().max(10)).optional(),
  roadTypeCode: z.string().max(5).optional(),
  roadTypeDescription: z.string().max(150).optional(),
  dates: permitDatesSchema.optional(),
  sensitivityFlags: sensitivityFlagsSchema.optional(),
});

// Traffic Management schema
const trafficManagementSchema = z.object({
  tmType: z.string().max(100).optional(),
  tmProvider: z.string().max(100).optional(),
  tmStart: z.string().datetime().nullable().optional(),
  tmEnd: z.string().datetime().nullable().optional(),
});

// Visit History schema
const completionCodesSchema = z.object({
  workCompletionReason: z.string().max(10).optional(),
  noAccessReason: z.string().max(20).optional(),
  gsrCodes: z.array(z.string().max(10)).optional(),
  jobOutcomeCode1: z.string().max(10).optional(),
  jobOutcomeCode2: z.string().max(10).optional(),
  jobOutcomeCode3: z.string().max(10).optional(),
  returnNightLine: z.string().max(5).optional(),
});

const visitFlagsSchema = z.object({
  chargesApplied: z.boolean().optional(),
  abandonedHeatCall: z.boolean().optional(),
  fullSafetyReport: z.boolean().optional(),
  fullWorkReport: z.boolean().optional(),
  workOnMeter: z.boolean().optional(),
  psrDiscussed: z.boolean().optional(),
});

const visitHistoryItemSchema = z.object({
  mobileKey: z.string().max(32),
  orderType: z.string(),
  taskType: z.string().max(30).optional(),
  jobDescription: z.string().max(500).optional(),
  fieldDate: z.string().datetime(),
  engineer: z.string().max(150).optional(),
  siteReport: z.string().max(2000).optional(),
  customer: z
    .object({
      name: z.string().max(150).optional(),
      phone: z.string().max(30).optional(),
    })
    .optional(),
  completionCodes: completionCodesSchema.optional(),
  flags: visitFlagsSchema.optional(),
});

// Street Risk Assessments schema
const streetRiskAssessmentSchema = z.object({
  mobileKey: z.string().max(32),
  orderType: z.string().max(10).optional(),
  comments: z.string().max(1000).optional(),
});

// Attachments schema
export const attachmentSchema = z.object({
  sourceOrderNumber: z.string().max(30).optional(),
  fileName: z.string().max(255),
  category: z.string().max(100).optional(),
  date: z.string().max(20).optional(),
  sourceUrl: z.string().max(2000),
  mimeType: z.string().max(100).optional(),
});

// Main payload schema
export const workOrderImportPayloadSchema = z.object({
  meta: metaSchema,
  workOrder: workOrderSchema,
  permit: permitSchema.nullable().optional(),
  trafficManagement: trafficManagementSchema.nullable().optional(),
  visitHistory: z.array(visitHistoryItemSchema).optional(),
  streetRiskAssessments: z.array(streetRiskAssessmentSchema).optional(),
  attachments: z.array(attachmentSchema).optional(),
});

/**
 * Success response schema
 */
export const workOrderImportResponseSchema = z.object({
  message: z.string(),
});

export type WorkOrderImportResponse = z.infer<typeof workOrderImportResponseSchema>;
export type WorkOrderImportPayload = z.infer<typeof workOrderImportPayloadSchema>;
